import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  InputAdornment,
  Chip,
  Rating,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import logo from '../assets/logo0.png';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import swalhiImg from '../assets/Swalhi.png';
import CategoryIcon from '@mui/icons-material/Category';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// Styled Components
const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000000',
  boxShadow: 'none',
  '& .MuiButton-root': {
    borderRadius: 0,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
  }
});

const SearchWrapper = styled(Box)({
  backgroundColor: '#f8f9fa',
  padding: '20px 0',
  borderBottom: '1px solid #e9ecef',
});

const ProductCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 0,
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
});

const HeroSection = styled(Box)({
  background: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)), url("/path/to/hero-bg.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: '80px 0',
  color: 'white',
});

const FilterSection = styled(Box)({
  backgroundColor: '#111',
  borderBottom: '1px solid rgba(255,227,2,0.1)',
});

// Add these global styles to remove border radius from all elements
const globalStyles = {
  '& .MuiButton-root': {
    borderRadius: 0,
  },
  '& .MuiCard-root': {
    borderRadius: 0,
  },
  '& .MuiTextField-root .MuiOutlinedInput-root': {
    borderRadius: 0,
  },
  '& .MuiSelect-root': {
    borderRadius: 0,
  },
  '& .MuiChip-root': {
    borderRadius: 0,
  },
  '& .MuiPaper-root': {
    borderRadius: 0,
  },
  '& .MuiInputBase-root': {
    borderRadius: 0,
  }
};

const Swalhi = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [currentTab, setCurrentTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Sample categories for the store
  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'engine', label: 'Engine Parts' },
    { value: 'transmission', label: 'Transmission' },
    { value: 'suspension', label: 'Suspension' },
    { value: 'brakes', label: 'Brakes' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'accessories', label: 'Accessories' },
  ];

  // Sample price ranges
  const priceRanges = [
    { value: 'all', label: 'All Prices' },
    { value: '0-5000', label: 'Under 5000 DZD' },
    { value: '5000-15000', label: '5000 - 15000 DZD' },
    { value: '15000-30000', label: '15000 - 30000 DZD' },
    { value: '30000+', label: 'Over 30000 DZD' },
  ];

  // Sample products data
  const products = [
    {
      id: 1,
      name: 'High Performance Brake Kit',
      category: 'brakes',
      price: 299.99,
      rating: 4.5,
      reviews: 128,
      image: 'https://example.com/brake-kit.jpg',
      description: 'Professional grade brake kit for optimal stopping power',
      inStock: true,
    },
    // Add more products here
  ];

  return (
    <Box sx={{ 
      bgcolor: '#000', 
      minHeight: '100vh',
      ...globalStyles // Apply global styles
    }}>
      {/* Enhanced Header */}
      <StyledAppBar position="fixed">
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1, px: { xs: 0, sm: 2 } }}>
            {/* Logo */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'white', 
              p: 1, 
              borderRadius: 0,
              mr: 2
            }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: '35px',
                  filter: 'none'
                }} 
              />
            </Box>

            {/* Desktop Menu */}
            <Box sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              gap: 2
            }}>
              {categories.map((cat) => (
                <Button
                  key={cat.value}
                  color="inherit"
                  sx={{
                    px: 2,
                    '&:hover': { 
                      color: '#FFE302',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    },
                  }}
                >
                  {cat.label}
                </Button>
              ))}
            </Box>

            {/* Desktop Actions */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center',
              gap: 2
            }}>
              <IconButton color="inherit">
                <Badge badgeContent={4} color="error">
                  <CartIcon />
                </Badge>
              </IconButton>
              <Button 
                variant="contained"
                startIcon={<PersonIcon />}
                sx={{ 
                  bgcolor: '#FFE302',
                  color: 'black',
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#e6cc00' }
                }}
              >
                Login
              </Button>
            </Box>

            {/* Mobile Actions */}
            <Box sx={{ 
              display: { xs: 'flex', md: 'none' }, 
              alignItems: 'center',
              gap: 1,
              ml: 'auto'
            }}>
              <IconButton color="inherit">
                <Badge badgeContent={4} color="error">
                  <CartIcon />
                </Badge>
              </IconButton>
              <IconButton 
                color="inherit" 
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              maxWidth: '300px',
              bgcolor: '#111',
              color: 'white',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Mobile Menu Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 3
            }}>
              <Box sx={{ 
                background: 'white', 
                p: 1
              }}>
                <img 
                  src={logo} 
                  alt="Logo" 
                  style={{ height: '30px' }} 
                />
              </Box>
              <IconButton 
                color="inherit" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Mobile Menu Items */}
            <List sx={{ mb: 2 }}>
              {categories.map((cat) => (
                <ListItem 
                  key={cat.value}
                  sx={{
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255,227,2,0.1)',
                    }
                  }}
                >
                  <Button
                    fullWidth
                    color="inherit"
                    sx={{
                      justifyContent: 'flex-start',
                      py: 1,
                      '&:hover': { 
                        color: '#FFE302',
                      }
                    }}
                  >
                    {cat.label}
                  </Button>
                </ListItem>
              ))}
            </List>

            {/* Mobile Menu Actions */}
            <Box sx={{ p: 2 }}>
              <Button 
                fullWidth
                variant="contained"
                startIcon={<PersonIcon />}
                sx={{ 
                  bgcolor: '#FFE302',
                  color: 'black',
                  mb: 2,
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#e6cc00' }
                }}
              >
                Login
              </Button>
              <Button 
                fullWidth
                variant="outlined"
                sx={{ 
                  color: '#FFE302',
                  borderColor: '#FFE302',
                  borderRadius: 0,
                  '&:hover': { 
                    borderColor: '#FFE302',
                    bgcolor: 'rgba(255,227,2,0.1)'
                  }
                }}
              >
                Register
              </Button>
            </Box>
          </Box>
        </Drawer>
      </StyledAppBar>
      <Toolbar /> {/* Spacing for fixed header */}

      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  '& span': { color: '#FFE302' }
                }}
              >
                Find Your Perfect <span>Auto Parts</span>
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', sm: '1.5rem' }
                }}
              >
                Quality parts, competitive prices, fast delivery
              </Typography>
              <Box sx={{ 
                display: 'flex',
                gap: 2,
                mb: 4,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <TextField
                  fullWidth
                  placeholder="Search parts..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      borderRadius: 0
                    }
                  }}
                />
                <Button 
                  variant="contained"
                  sx={{ 
                    bgcolor: '#FFE302',
                    color: 'black',
                    px: 4,
                    borderRadius: 0,
                    '&:hover': { bgcolor: '#e6cc00' }
                  }}
                >
                  Search
                </Button>
              </Box>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="h4" sx={{ color: '#FFE302', fontWeight: 700 }}>
                    50K+
                  </Typography>
                  <Typography variant="body1">Parts Available</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: '#FFE302', fontWeight: 700 }}>
                    24/7
                  </Typography>
                  <Typography variant="body1">Support</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: '#FFE302', fontWeight: 700 }}>
                    98%
                  </Typography>
                  <Typography variant="body1">Satisfaction</Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* Hero Image - Hide on very small screens */}
            <Grid 
              item 
              xs={12} 
              md={6} 
              sx={{ 
                display: { xs: 'none', sm: 'block' }
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '@keyframes float': {
                    '0%, 100%': {
                      transform: 'translateY(0px)',
                    },
                    '50%': {
                      transform: 'translateY(-20px)',
                    },
                  },
                  animation: 'float 6s ease-in-out infinite',
                }}
              >
                <Box
                  component="img"
                  src={swalhiImg}
                  alt="Swalhi Parts"
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: { sm: '300px', md: '500px' },
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Filter Section */}
      <FilterSection>
        <Container maxWidth="lg">
          <Grid container spacing={2} sx={{ py: 3 }}>
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                displayEmpty
                startAdornment={
                  <CategoryIcon sx={{ 
                    color: '#FFE302', 
                    mr: 1,
                    ml: 1 
                  }} />
                }
                sx={{
                  bgcolor: '#111',
                  color: 'white',
                  borderRadius: 0,
                  '& .MuiSelect-select': { 
                    py: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,227,2,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FFE302',
                  },
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                displayEmpty
                startAdornment={
                  <MonetizationOnIcon sx={{ 
                    color: '#FFE302', 
                    mr: 1,
                    ml: 1 
                  }} />
                }
                sx={{
                  bgcolor: '#111',
                  color: 'white',
                  borderRadius: 0,
                  '& .MuiSelect-select': { 
                    py: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,227,2,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FFE302',
                  },
                }}
              >
                {priceRanges.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Select
                fullWidth
                displayEmpty
                defaultValue=""
                startAdornment={
                  <DirectionsCarIcon sx={{ 
                    color: '#FFE302', 
                    mr: 1,
                    ml: 1 
                  }} />
                }
                sx={{
                  bgcolor: '#111',
                  color: 'white',
                  borderRadius: 0,
                  '& .MuiSelect-select': { 
                    py: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,227,2,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#FFE302',
                  },
                }}
              >
                <MenuItem value="">Brand</MenuItem>
                <MenuItem value="toyota">Toyota</MenuItem>
                <MenuItem value="honda">Honda</MenuItem>
                <MenuItem value="ford">Ford</MenuItem>
                <MenuItem value="volkswagen">Volkswagen</MenuItem>
                <MenuItem value="mercedes">Mercedes</MenuItem>
                <MenuItem value="bmw">BMW</MenuItem>
              </Select>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<FilterAltIcon />}
                sx={{
                  bgcolor: '#FFE302',
                  color: 'black',
                  py: 1.5,
                  borderRadius: 0,
                  fontSize: '1rem',
                  fontWeight: 600,
                  border: '2px solid #FFE302',
                  '&:hover': { 
                    bgcolor: '#111',
                    color: '#FFE302',
                    border: '2px solid #FFE302'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                APPLY FILTERS
              </Button>
            </Grid>
          </Grid>
        </Container>
      </FilterSection>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Filters and Sort */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Button startIcon={<FilterIcon />} sx={{ mr: 1 }}>
              Filters
            </Button>
            <Button startIcon={<SortIcon />}>
              Sort
            </Button>
          </Box>
          <Box>
            <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'}>
              <GridIcon />
            </IconButton>
            <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'}>
              <ListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item key={product.id} xs={12} sm={6} md={viewMode === 'grid' ? 4 : 12}>
              <ProductCard>
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image}
                  alt={product.name}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {product.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Rating value={product.rating} precision={0.5} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({product.reviews})
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary">
                    {product.price.toLocaleString()} DZD
                  </Typography>
                  <Chip
                    label={product.inStock ? 'In Stock' : 'Out of Stock'}
                    color={product.inStock ? 'success' : 'error'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
                <CardActions sx={{ mt: 'auto' }}>
                  <Button size="small" startIcon={<CartIcon />}>
                    Add to Cart
                  </Button>
                  <Button size="small" startIcon={<CommentIcon />}>
                    Reviews
                  </Button>
                  <IconButton size="small">
                    <FavoriteIcon />
                  </IconButton>
                </CardActions>
              </ProductCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Enhanced Footer */}
      <Box sx={{ bgcolor: '#111', color: 'white', pt: 8, pb: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                background: 'white',
                p: 1,
                borderRadius: 1,
                width: 'fit-content'
              }}>
                <img src={logo} alt="Logo" style={{ height: '40px' }} />
              </Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Your one-stop destination for quality auto parts and accessories.
                We provide genuine parts with warranty and expert support.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Social Media Icons */}
                <IconButton sx={{ color: '#FFE302' }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton sx={{ color: '#FFE302' }}>
                  <TwitterIcon />
                </IconButton>
                <IconButton sx={{ color: '#FFE302' }}>
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFE302' }}>
                Quick Links
              </Typography>
              <List>
                {['About Us', 'Contact', 'Blog', 'FAQ'].map((text) => (
                  <ListItem key={text} sx={{ py: 0.5 }}>
                    <ListItemText 
                      primary={text}
                      sx={{ 
                        '& .MuiTypography-root': { 
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { color: '#FFE302' }
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFE302' }}>
                Contact Info
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                1234 Auto Parts Street
                <br />
                City, State 12345
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Phone: (123) 456-7890
              </Typography>
              <Typography variant="body2">
                Email: info@swalhiparts.com
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FFE302' }}>
                Newsletter
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Subscribe for updates on new products and special offers.
              </Typography>
              <TextField
                fullWidth
                placeholder="Your email"
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    borderRadius: 0,
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.1)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#FFE302'
                    }
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: '#FFE302',
                  color: 'black',
                  borderRadius: 0,
                  '&:hover': { bgcolor: '#e6cc00' }
                }}
              >
                Subscribe
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            © {new Date().getFullYear()} Swalhi Auto Parts. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Swalhi; 